import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useAuth } from "../auth/AuthContext";
import { contarNaoLidasChat, obterEstadoNaoLidasChat, type EstadoNaoLidasChat } from "../api/chatInterno";
import { obterPreferenciasNotificacoesUsuario } from "../api/usuario";
import { obterNotificacoesPainel, obterTotalNotificacoesPainel } from "../api/notificacoes";
import { CentralNotificacoes } from "../components/notificacoes/CentralNotificacoes";
import { ChatInterno } from "../components/chat/ChatInterno";
import { definirSomNotificacoesAtivo } from "../utils/somNovaMensagem";
import { avisarNotificacao, definirPushNotificacoesAtivo, pushNotificacoesAtivo } from "../utils/avisoNotificacao";
import { subscribeRealtime } from "../realtime/realtimeClient";
import { usuarioTemAcessoTela } from "../utils/permissoes";
import { normalizarChaveTela } from "./menuAcesso";
import { ROTAS_RECURSOS_PLATAFORMA } from "./telasDisponiveis";

interface AcoesTopo {
  abrirIA: () => void;
  abrirSuporteIA: () => void;
  abrirChat: () => void;
  abrirNotificacoes: () => void;
  totalNotificacoes: number;
  naoLidasChat: number;
  iaVisivel: boolean;
  iaAtiva: boolean;
}

const Contexto = createContext<AcoesTopo>({
  abrirIA: () => undefined,
  abrirSuporteIA: () => undefined,
  abrirChat: () => undefined,
  abrirNotificacoes: () => undefined,
  totalNotificacoes: 0,
  naoLidasChat: 0,
  iaVisivel: false,
  iaAtiva: false,
});

export function useAcoesTopo(): AcoesTopo {
  return useContext(Contexto);
}

interface AcoesTopoProviderProps {
  iaVisivel: boolean;
  iaAtiva: boolean;
  onAbrirIA: () => void;
  children: ReactNode;
}

export function AcoesTopoProvider({ iaVisivel, iaAtiva, onAbrirIA, children }: AcoesTopoProviderProps) {
  const { usuario } = useAuth();
  const podeUsarAgente = usuarioTemAcessoTela(usuario, normalizarChaveTela(ROTAS_RECURSOS_PLATAFORMA.agente));
  const podeUsarChat = usuarioTemAcessoTela(usuario, normalizarChaveTela(ROTAS_RECURSOS_PLATAFORMA.chat));
  const podeUsarCentralNotificacoes = usuarioTemAcessoTela(usuario, normalizarChaveTela(ROTAS_RECURSOS_PLATAFORMA.centralNotificacoes));
  const podeConfigurarNotificacoes = usuarioTemAcessoTela(usuario, normalizarChaveTela(ROTAS_RECURSOS_PLATAFORMA.preferenciasNotificacoes));
  const [notificacoesAbertas, setNotificacoesAbertas] = useState(false);
  const [chatAberto, setChatAberto] = useState(false);
  const [totalNotificacoes, setTotalNotificacoes] = useState(0);
  const [naoLidasChat, setNaoLidasChat] = useState(0);
  const estadoChatAnteriorRef = useRef<EstadoNaoLidasChat | null>(null);
  const ultimaNotificacaoRef = useRef<string | null>(null);
  const carregandoNotificacoesRef = useRef(false);

  useEffect(() => {
    if (!usuario?.codLogin || (!podeUsarCentralNotificacoes && !podeConfigurarNotificacoes)) return;
    void obterPreferenciasNotificacoesUsuario()
      .then((preferencias) => {
        definirSomNotificacoesAtivo(preferencias.somAtivo);
        definirPushNotificacoesAtivo(preferencias.canais.push !== false);
      })
      .catch(() => undefined);
  }, [podeConfigurarNotificacoes, podeUsarCentralNotificacoes, usuario?.codLogin]);

  useEffect(() => {
    // Cada escopo começa sem evento anterior, evitando avisos de outra sessão/empresa.
    ultimaNotificacaoRef.current = null;
    if (!podeUsarCentralNotificacoes) {
      setNotificacoesAbertas(false);
      setTotalNotificacoes(0);
      return;
    }
    let ativo = true;
    const carregar = async () => {
      if (carregandoNotificacoesRef.current) return;
      carregandoNotificacoesRef.current = true;
      const resultado = await obterTotalNotificacoesPainel().catch(() => null);
      try {
        if (!ativo || !resultado) return;
        setTotalNotificacoes(resultado.total);

        const anterior = ultimaNotificacaoRef.current;
        ultimaNotificacaoRef.current = resultado.latestKey;
        const idsAtuais = resultado.latestKey.split(":").map(Number);
        const idsAnteriores = String(anterior || "").split(":").map(Number);
        const recebeuNova = anterior !== null
          && idsAtuais.some((id, indice) => id > (idsAnteriores[indice] || 0));
        /* A primeira carga só marca o ponto de partida; as seguintes respeitam
           imediatamente o canal Push salvo no modal de preferências. */
        if (!recebeuNova || !pushNotificacoesAtivo()) return;

        const painel = await obterNotificacoesPainel().catch(() => null);
        if (!ativo) return;
        // IDs pertencem a categorias distintas. Só anunciar a categoria que recebeu um evento.
        const indiceNovo = [2, 0, 1].find((indice) => idsAtuais[indice] > (idsAnteriores[indice] || 0));
        const categorias = painel ? [painel.atividades, painel.novidades, painel.avisos] : [];
        const recente = indiceNovo === undefined ? null
          : categorias[indiceNovo]?.find((item) => Number(item.id) > (idsAnteriores[indiceNovo] || 0));
        if (!recente) return;
        avisarNotificacao(String(recente?.titulo || "").trim(), String(recente?.texto || "").trim());
      } finally {
        carregandoNotificacoesRef.current = false;
      }
    };
    void carregar();
    const cancelarRealtime = subscribeRealtime(["novaMensagemAtendimento"], () => void carregar());
    const intervalo = window.setInterval(() => void carregar(), 15_000);
    return () => { ativo = false; cancelarRealtime(); window.clearInterval(intervalo); };
  }, [podeUsarCentralNotificacoes, usuario?.codLogin, usuario?.codEmpresa, usuario?.codOrg, usuario?.selectedCodOrg]);

  useEffect(() => {
    if (!podeUsarChat) {
      setChatAberto(false);
      setNaoLidasChat(0);
      return;
    }
    estadoChatAnteriorRef.current = null;
    let ativo = true;
    const carregar = async () => {
      const estado = await obterEstadoNaoLidasChat().catch(() => null);
      if (!ativo) return;
      if (!estado) return;
      const anterior = estadoChatAnteriorRef.current;
      const recebeuNovoEvento = anterior !== null && (
        estado.ultimaMensagemRecebidaId > anterior.ultimaMensagemRecebidaId
        || estado.ultimaConversaRecebidaId > anterior.ultimaConversaRecebidaId
      );
      if (!chatAberto && recebeuNovoEvento) {
        /* Mesma entrega dos demais avisos: som sempre, toast com Push ligado. */
        avisarNotificacao("Nova mensagem no chat interno");
        setChatAberto(true);
      }
      estadoChatAnteriorRef.current = estado;
      setNaoLidasChat(estado.total);
    };
    void carregar();
    const intervalo = window.setInterval(() => void carregar(), 5_000);
    return () => { ativo = false; window.clearInterval(intervalo); };
  }, [chatAberto, podeUsarChat, usuario?.codEmpresa, usuario?.codOrg]);

  useEffect(() => {
    const abrir = () => { if (podeUsarCentralNotificacoes) setNotificacoesAbertas(true); };
    const fechar = () => setNotificacoesAbertas(false);
    window.addEventListener("system-tour-open-notifications", abrir);
    window.addEventListener("system-tour-close-notifications", fechar);
    return () => {
      window.removeEventListener("system-tour-open-notifications", abrir);
      window.removeEventListener("system-tour-close-notifications", fechar);
    };
  }, [podeUsarCentralNotificacoes]);

  const abrirIA = useCallback(() => { if (podeUsarAgente && iaVisivel && iaAtiva) onAbrirIA(); }, [iaAtiva, iaVisivel, onAbrirIA, podeUsarAgente]);
  const abrirSuporteIA = useCallback(() => { if (podeUsarAgente) onAbrirIA(); }, [onAbrirIA, podeUsarAgente]);
  const abrirChat = useCallback(() => { if (podeUsarChat) setChatAberto(true); }, [podeUsarChat]);
  const abrirNotificacoes = useCallback(() => { if (podeUsarCentralNotificacoes) setNotificacoesAbertas(true); }, [podeUsarCentralNotificacoes]);

  const valor = useMemo<AcoesTopo>(() => ({
    abrirIA,
    abrirSuporteIA,
    abrirChat,
    abrirNotificacoes,
    totalNotificacoes,
    naoLidasChat,
    iaVisivel,
    iaAtiva,
  }), [abrirChat, abrirIA, abrirNotificacoes, abrirSuporteIA, iaAtiva, iaVisivel, naoLidasChat, totalNotificacoes]);

  return (
    <Contexto.Provider value={valor}>
      {children}
      {podeUsarCentralNotificacoes && <CentralNotificacoes aberto={notificacoesAbertas} onFechar={() => setNotificacoesAbertas(false)} />}
      {podeUsarChat && <ChatInterno
        key={`${usuario?.codOrg ?? ""}:${usuario?.codEmpresa ?? ""}`}
        aberto={chatAberto}
        onFechar={() => {
          setChatAberto(false);
          void contarNaoLidasChat().then(setNaoLidasChat).catch(() => undefined);
        }}
      />}
    </Contexto.Provider>
  );
}
